import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalIcon, Plus, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function CalendarPage() {
  const [meetings, setMeetings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    lead_id: "", title: "Llamada de descubrimiento",
    start_at: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    duration_min: 30, notes: "",
  });

  const load = () => {
    api.get("/calendar/meetings").then((r) => setMeetings(r.data));
    api.get("/leads").then((r) => setLeads(r.data));
    api.get("/calendar/google/status").then((r) => setStatus(r.data));
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.lead_id) return toast.error("Selecciona un lead");
    await api.post("/calendar/meetings", {
      ...form,
      start_at: new Date(form.start_at).toISOString(),
      duration_min: Number(form.duration_min),
    });
    toast.success("Reunión agendada");
    setOpen(false);
    load();
  };

  const fmt = (iso) => new Date(iso).toLocaleString("es", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="stagger">
      <PageHeader
        overline="Calendario"
        title="Agendar llamadas."
        subtitle="Programa citas con los leads calificados. Sincronización con Google Calendar disponible bajo demanda."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="meeting-create-button" className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
                <Plus className="w-4 h-4 mr-2" />Nueva reunión
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-sm">
              <DialogHeader><DialogTitle className="font-display">Agendar llamada</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Lead</Label>
                  <Select value={form.lead_id} onValueChange={(v) => setForm({ ...form, lead_id: v })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="meeting-lead-select"><SelectValue placeholder="Selecciona un lead" /></SelectTrigger>
                    <SelectContent>
                      {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} · {l.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" /></div>
                <div><Label>Fecha y hora</Label><Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="mt-1 rounded-sm" /></div>
                <div><Label>Duración (min)</Label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1 rounded-sm" /></div>
                <div><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="meeting-create-submit" onClick={create} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">Agendar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {status && !status.connected && (
        <div className="mb-6 p-4 border border-[#E2DCD0] bg-[#F9F8F6] rounded-sm flex gap-3">
          <AlertCircle className="w-4 h-4 text-[#D95D39] shrink-0 mt-0.5" />
          <p className="text-sm text-[#5C616A]">{status.message}</p>
        </div>
      )}

      <div className="space-y-3">
        {meetings.length === 0 && (
          <Card className="edit-card p-12 rounded-sm shadow-none text-center text-[#5C616A]">
            Aún no hay reuniones agendadas.
          </Card>
        )}
        {meetings.map((m) => (
          <Card key={m.id} className="edit-card p-5 rounded-sm shadow-none flex items-start justify-between gap-4">
            <div className="flex gap-4 min-w-0">
              <div className="w-12 h-12 rounded-sm bg-[#0A1128] flex flex-col items-center justify-center text-white shrink-0">
                <CalIcon className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <h4 className="font-display text-lg text-[#0A1128] truncate">{m.title}</h4>
                <p className="text-sm text-[#5C616A]">{m.lead_name} · {m.lead_email}</p>
                <p className="text-xs text-[#8A8F98] mt-1 font-mono">{fmt(m.start_at)} · {m.duration_min} min</p>
              </div>
            </div>
            <Badge variant="outline" className="rounded-sm border-[#D95D39] text-[#D95D39]">{m.status}</Badge>
          </Card>
        ))}
      </div>
    </div>
  );
}
